import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminLike } from "@/lib/roles";
import { fmtDate } from "@/lib/dates";
import { toast } from "sonner";
import { Trophy, Plus, Pencil, Trash2, Users, CalendarDays, Flag, ListOrdered, CalendarCheck, CalendarX, Swords, ChevronRight, X, Star } from "lucide-react";

const CATS = [
  { key: "flag_femenino", label: "Flag Femenino" },
  { key: "flag_masculino", label: "Flag Masculino" },
  { key: "tackle", label: "Tackle" },
];
const CAT_LABEL = Object.fromEntries(CATS.map(c => [c.key, c.label]));
const STATUS_COLOR = { draft: "#71717A", active: "#34C759", published: "#34C759", finished: "#06B6D4", archived: "#52525B", scheduled: "#A1A1AA", live: "#FACC15", postponed: "#FF9F0A", cancelled: "#FF3B30" };

const fmtPct = (v) => (v == null || Number.isNaN(v)) ? ".000" : Number(v).toFixed(3).replace(/^0/, "");
const RESULT_COLOR = { W: "#34C759", L: "#FF3B30", T: "#FF9F0A" };
const RESULT_ES = { W: "G", L: "P", T: "E" };

export default function Fixture() {
  const { user } = useAuth();
  const [season, setSeason] = useState("2026");
  const [seasons, setSeasons] = useState(["2026"]);
  const [category, setCategory] = useState("flag_femenino");
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompId, setSelectedCompId] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  // Vistas: "browse" (Competencias/Catálogo/Rivales), o dentro de una comp: "summary" | "teams" | "calendar" | "standings" | "config"
  const [browseTab, setBrowseTab] = useState("competitions"); // competitions | catalog | rivals
  const [compTab, setCompTab] = useState("summary");
  const canAdmin = useMemo(() => {
    if (isAdminLike(user?.role) && user?.role !== "admin_treasurer") return true;
    if (user?.role === "coach") return (user?.coach_categories || []).includes(category);
    return false;
  }, [user, category]);

  // Cargar temporadas disponibles
  useEffect(() => {
    api.get("/fixture/seasons").then(r => {
      const list = r.data.seasons || [];
      const merged = list.length ? list : ["2026"];
      setSeasons(merged);
      if (!merged.includes(season)) setSeason(merged[0]);
    }).catch(() => setSeasons(["2026"]));
  }, []); // eslint-disable-line

  // Cargar competencias cuando cambia temporada/rama/archivadas
  useEffect(() => {
    const q = new URLSearchParams({ category, season, include_archived: String(includeArchived) });
    api.get(`/fixture/competitions?${q}`).then(r => {
      setCompetitions(r.data);
      // Si la comp seleccionada ya no está, deseleccionar
      if (selectedCompId && !r.data.some(c => c.id === selectedCompId)) setSelectedCompId("");
    }).catch(() => toast.error("Error cargando competencias"));
  }, [category, season, includeArchived]); // eslint-disable-line

  const selectedComp = competitions.find(c => c.id === selectedCompId) || null;

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8" data-testid="fixture-page">
        <div className="overline text-cyan-400 flex items-center gap-2"><Trophy size={13}/> Módulo Fixture</div>
        <h1 className="font-display text-4xl sm:text-5xl text-white leading-none mt-2">COMPETENCIAS</h1>
        <p className="text-[#A1A1AA] mt-2 max-w-2xl text-sm">Administra temporadas, ramas, competencias, equipos, jornadas y partidos.</p>

        {/* Selectores jerárquicos */}
        <div className="tactical-card p-3 md:p-4 mt-6 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <div className="overline text-[#A1A1AA] text-[10px] mb-1">Temporada</div>
            <select value={season} onChange={e => { setSeason(e.target.value); setSelectedCompId(""); }} className="input-tactical w-full" data-testid="sel-season">
              {seasons.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div className="overline text-[#A1A1AA] text-[10px] mb-1">Rama</div>
            <select value={category} onChange={e => { setCategory(e.target.value); setSelectedCompId(""); }} className="input-tactical w-full" data-testid="sel-category">
              {CATS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <div className="overline text-[#A1A1AA] text-[10px] mb-1 flex justify-between">
              <span>Competencia</span>
              <label className="flex items-center gap-1 text-[9px] cursor-pointer">
                <input type="checkbox" checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)} data-testid="chk-archived"/>
                <span>Incluir archivadas</span>
              </label>
            </div>
            <select value={selectedCompId} onChange={e => { setSelectedCompId(e.target.value); setCompTab("summary"); }} className="input-tactical w-full" data-testid="sel-competition">
              <option value="">— Ver listado / catálogo —</option>
              {competitions.map(c => <option key={c.id} value={c.id}>{c.is_archived ? "🗄️ " : ""}{c.name} · {c.season}</option>)}
            </select>
          </div>
        </div>

        {/* MODO A: Sin competencia seleccionada → tabs de navegación general */}
        {!selectedComp && (
          <>
            <div className="flex gap-2 mt-4 overflow-x-auto no-scrollbar -mx-6 px-6 md:mx-0 md:px-0 md:flex-wrap">
              {[
                ["competitions", "Competencias", Trophy],
                ["catalog", "Catálogo Equipos", Users],
                ["rivals", "Rivales H2H", Swords],
              ].map(([k, l, Icon]) => (
                <button key={k} onClick={() => setBrowseTab(k)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors flex items-center gap-1.5 whitespace-nowrap ${browseTab === k ? "border-cyan-400 text-cyan-400" : "border-[#27272A] text-[#A1A1AA] hover:text-white"}`}
                        data-testid={`tab-browse-${k}`}>
                  <Icon size={12}/> {l}
                </button>
              ))}
            </div>
            <div className="mt-6">
              {browseTab === "competitions" && <CompetitionsPanel category={category} season={season} includeArchived={includeArchived} canAdmin={canAdmin} onOpen={cid => { setSelectedCompId(cid); setCompTab("summary"); }}/>}
              {browseTab === "catalog" && <TeamsPanel category={category} canAdmin={canAdmin}/>}
              {browseTab === "rivals" && <RivalsPanel category={category}/>}
            </div>
          </>
        )}

        {/* MODO B: Competencia seleccionada → header + 5 tabs internos */}
        {selectedComp && (
          <>
            <div className="tactical-card p-4 mt-4 flex flex-wrap items-center justify-between gap-3" data-testid="comp-header">
              <div className="min-w-0">
                <button onClick={() => setSelectedCompId("")} className="text-[10px] uppercase tracking-widest text-[#71717A] hover:text-cyan-400 flex items-center gap-1 mb-1" data-testid="comp-back"><ChevronRight size={12} className="rotate-180"/> Volver al listado</button>
                <div className="font-display text-xl md:text-2xl text-white truncate">{selectedComp.is_archived && <span className="text-[#71717A]">🗄️ </span>}{selectedComp.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#71717A]">
                  {selectedComp.season} · {CAT_LABEL[selectedComp.category]} · <span style={{color: STATUS_COLOR[selectedComp.status] || "#A1A1AA"}}>{selectedComp.status}</span>
                  {selectedComp.is_archived && <span className="ml-2 text-[#FF9F0A]">ARCHIVADA</span>}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-4 overflow-x-auto no-scrollbar -mx-6 px-6 md:mx-0 md:px-0 md:flex-wrap">
              {[
                ["summary", "Resumen", ListOrdered],
                ["teams", "Equipos", Users],
                ["calendar", "Calendario", CalendarDays],
                ["standings", "Tabla", Trophy],
                ["config", "Configuración", Flag],
              ].map(([k, l, Icon]) => (
                <button key={k} onClick={() => setCompTab(k)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors flex items-center gap-1.5 whitespace-nowrap ${compTab === k ? "border-cyan-400 text-cyan-400" : "border-[#27272A] text-[#A1A1AA] hover:text-white"}`}
                        data-testid={`tab-comp-${k}`}>
                  <Icon size={12}/> {l}
                </button>
              ))}
            </div>

            <div className="mt-6">
              {compTab === "summary" && <CompetitionSummary comp={selectedComp}/>}
              {compTab === "teams" && <EnrolledTeamsPanel comp={selectedComp} canAdmin={canAdmin}/>}
              {compTab === "calendar" && <CalendarByRoundsPanel comp={selectedComp} canAdmin={canAdmin}/>}
              {compTab === "standings" && <CompetitionStandings comp={selectedComp}/>}
              {compTab === "config" && <CompetitionConfig comp={selectedComp} canAdmin={canAdmin} onDeleted={() => setSelectedCompId("")}/>}
            </div>
          </>
        )}

        <div className="h-20 md:h-6"/>
      </div>
    </Layout>
  );
}

// ---------- Competencias ----------
function CompetitionsPanel({ category, season, includeArchived, canAdmin, onOpen }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => {
    const q = new URLSearchParams({ category, season, include_archived: String(includeArchived) });
    return api.get(`/fixture/competitions?${q}`).then(r => setItems(r.data)).catch(() => toast.error("Error"));
  };
  useEffect(() => { load(); }, [category, season, includeArchived]); // eslint-disable-line
  const remove = async (c) => {
    if (!window.confirm(`Eliminar la competencia "${c.name}"? Esto BORRA en cascada todas sus fechas, partidos y sus eventos en el calendario del club. Esta acción no se puede deshacer.`)) return;
    try { await api.delete(`/fixture/competitions/${c.id}`); toast.success("Competencia eliminada"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };
  const pageSize = 10;
  const { page, setPage, pages, total, slice } = usePaginated(items, pageSize, category);
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs uppercase tracking-widest text-[#A1A1AA]">{CAT_LABEL[category]} · {items.length} competencia(s)</div>
        {canAdmin && <button onClick={() => setCreating(true)} className="btn-primary text-xs flex items-center gap-1" data-testid="new-competition"><Plus size={12}/> Nueva</button>}
      </div>
      <SimpleTable
        cols={[
          { k: "name", l: "Nombre" },
          { k: "season", l: "Temporada" },
          { k: "status", l: "Estado", badge: true },
          { k: "is_archived", l: "Archiv.", fmt: v => v ? "🗄️" : "" },
          { k: "start_date", l: "Inicio", fmt: fmtDate },
          { k: "end_date", l: "Fin", fmt: fmtDate },
        ]}
        rows={slice} canAdmin={canAdmin} onEdit={setEditing} onDelete={remove}
        onRowClick={onOpen ? (c) => onOpen(c.id) : undefined}
        deleteLabel="Eliminar" testidPrefix="cmp"/>
      <Pagination page={page} pages={pages} total={total} pageSize={pageSize} onPage={setPage} label="competencia(s)" testid="cmp-pagination"/>
      {(editing || creating) && (
        <CompetitionModal category={category} initial={editing} onClose={() => { setEditing(null); setCreating(false); }} onSaved={() => { setEditing(null); setCreating(false); load(); }}/>
      )}
    </div>
  );
}

function CompetitionModal({ category, initial, onClose, onSaved }) {
  const [d, setD] = useState(initial || { name: "", category, season: `${new Date().getFullYear()}`, start_date: "", end_date: "", points_win: 3, points_draw: 1, points_loss: 0, allow_draws: true, status: "draft" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      if (initial) await api.patch(`/fixture/competitions/${initial.id}`, d);
      else await api.post("/fixture/competitions", d);
      toast.success("Guardado"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title={initial ? "Editar competencia" : "Nueva competencia"} onClose={onClose}>
      <Field label="Nombre *"><input required className="input-tactical" value={d.name} onChange={e => setD({ ...d, name: e.target.value })} data-testid="cmp-name"/></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Temporada"><input className="input-tactical" value={d.season} onChange={e => setD({ ...d, season: e.target.value })}/></Field>
        <Field label="Estado">
          <select className="input-tactical" value={d.status} onChange={e => setD({ ...d, status: e.target.value })}>
            <option value="draft">Borrador</option><option value="active">Activa</option><option value="finished">Finalizada</option><option value="archived">Archivada</option>
          </select>
        </Field>
        <Field label="Fecha inicio"><input type="date" className="input-tactical" value={d.start_date || ""} onChange={e => setD({ ...d, start_date: e.target.value })}/></Field>
        <Field label="Fecha fin"><input type="date" className="input-tactical" value={d.end_date || ""} onChange={e => setD({ ...d, end_date: e.target.value })}/></Field>
      </div>
      <ModalFooter onClose={onClose} onSave={save} saving={saving}/>
    </ModalShell>
  );
}

// ---------- Equipos ----------
function TeamsPanel({ category, canAdmin }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => api.get(`/fixture/teams?category=${category}`).then(r => setItems(r.data)).catch(() => toast.error("Error"));
  useEffect(() => { load(); }, [category]); // eslint-disable-line
  const deactivate = async (t) => {
    if (!window.confirm(`Desactivar "${t.name}"? No se elimina físicamente si tiene partidos asociados.`)) return;
    try { await api.delete(`/fixture/teams/${t.id}`); toast.success("Desactivado"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };
  const pageSize = 15;
  const { page, setPage, pages, total, slice } = usePaginated(items, pageSize, category);
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs uppercase tracking-widest text-[#A1A1AA]">{CAT_LABEL[category]} · {items.length} equipo(s)</div>
        {canAdmin && <button onClick={() => setCreating(true)} className="btn-primary text-xs flex items-center gap-1" data-testid="new-team"><Plus size={12}/> Nuevo equipo</button>}
      </div>
      <SimpleTable
        cols={[
          { k: "name", l: "Nombre" },
          { k: "short_name", l: "Sigla" },
          { k: "city", l: "Ciudad" },
          { k: "is_ferroviarios", l: "Ferro?", fmt: v => v ? "★" : "" },
          { k: "active", l: "Activo", fmt: v => v ? "Sí" : "No" },
        ]}
        rows={slice} canAdmin={canAdmin} onEdit={setEditing} onDelete={deactivate} deleteLabel="Desactivar" testidPrefix="tm"/>
      <Pagination page={page} pages={pages} total={total} pageSize={pageSize} onPage={setPage} label="equipo(s)" testid="tm-pagination"/>
      {(editing || creating) && (
        <TeamModal category={category} initial={editing} onClose={() => { setEditing(null); setCreating(false); }} onSaved={() => { setEditing(null); setCreating(false); load(); }}/>
      )}
    </div>
  );
}

function TeamModal({ category, initial, onClose, onSaved }) {
  const [d, setD] = useState(initial || { name: "", short_name: "", category, city: "", is_ferroviarios: false, active: true, color_primary: "", color_secondary: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      if (initial) await api.patch(`/fixture/teams/${initial.id}`, d);
      else await api.post("/fixture/teams", d);
      toast.success("Guardado"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title={initial ? "Editar equipo" : "Nuevo equipo"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nombre *"><input required className="input-tactical" value={d.name} onChange={e => setD({ ...d, name: e.target.value })} data-testid="tm-name"/></Field>
        <Field label="Sigla / corto"><input className="input-tactical" value={d.short_name || ""} onChange={e => setD({ ...d, short_name: e.target.value })}/></Field>
        <Field label="Ciudad"><input className="input-tactical" value={d.city || ""} onChange={e => setD({ ...d, city: e.target.value })}/></Field>
        <Field label="Color primario"><input type="color" className="input-tactical h-10 p-1" value={d.color_primary || "#000000"} onChange={e => setD({ ...d, color_primary: e.target.value })}/></Field>
      </div>
      <label className="flex items-center gap-2 mt-3"><input type="checkbox" checked={d.is_ferroviarios} onChange={e => setD({ ...d, is_ferroviarios: e.target.checked })}/><span className="text-sm text-white">Este es el equipo de Ferroviarios</span></label>
      <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={d.active} onChange={e => setD({ ...d, active: e.target.checked })}/><span className="text-sm text-white">Activo</span></label>
      <ModalFooter onClose={onClose} onSave={save} saving={saving}/>
    </ModalShell>
  );
}

// ---------- Fechas ----------
function RoundsPanel({ category, canAdmin }) {
  const [items, setItems] = useState([]);
  const [comps, setComps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [publishing, setPublishing] = useState(null); // rid en curso
  const load = async () => {
    const [r, c] = await Promise.all([
      api.get(`/fixture/rounds?category=${category}`),
      api.get(`/fixture/competitions?category=${category}`),
    ]);
    setItems(r.data); setComps(c.data);
  };
  useEffect(() => { load().catch(() => toast.error("Error")); }, [category]); // eslint-disable-line
  const compName = (id) => comps.find(c => c.id === id)?.name || "—";
  const pageSize = 15;
  const { page, setPage, pages, total, slice } = usePaginated(items, pageSize, category);
  const publishRound = async (rnd) => {
    if (!window.confirm(`Publicar "${rnd.name}" al calendario del club? Todos los partidos se verán en el calendario de los usuarios conectados.`)) return;
    setPublishing(rnd.id);
    try {
      const r = await api.post(`/fixture/rounds/${rnd.id}/publish_to_calendar`);
      toast.success(`${r.data.matches_synced} partido(s) publicado(s)`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al publicar"); }
    finally { setPublishing(null); }
  };
  const unpublishRound = async (rnd) => {
    if (!window.confirm(`Retirar "${rnd.name}" del calendario del club? Se borrarán todos los eventos asociados.`)) return;
    setPublishing(rnd.id);
    try {
      const r = await api.post(`/fixture/rounds/${rnd.id}/unpublish_from_calendar`);
      toast.success(`${r.data.matches_removed} evento(s) removido(s)`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al retirar"); }
    finally { setPublishing(null); }
  };
  const removeRound = async (rnd) => {
    if (!window.confirm(`Eliminar la fecha "${rnd.name}"? Esto BORRA todos sus partidos y sus eventos del calendario. No se puede deshacer.`)) return;
    try {
      const r = await api.delete(`/fixture/rounds/${rnd.id}`);
      toast.success(`Fecha eliminada · ${r.data.matches_removed} partido(s) borrados`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs uppercase tracking-widest text-[#A1A1AA]">{items.length} fecha(s)</div>
        {canAdmin && <button onClick={() => setCreating(true)} className="btn-primary text-xs flex items-center gap-1" disabled={comps.length === 0} data-testid="new-round"><Plus size={12}/> Nueva fecha</button>}
      </div>
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#27272A] text-left">
            <th className="p-3 overline">Nombre</th>
            <th className="p-3 overline">Competencia</th>
            <th className="p-3 overline">Fecha</th>
            <th className="p-3 overline">Fase</th>
            <th className="p-3 overline">Estado</th>
            {canAdmin && <th className="p-3 overline text-right">Acciones</th>}
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={canAdmin ? 6 : 5} className="p-10 text-center text-[#71717A]">Sin fechas.</td></tr>}
            {slice.map(r => {
              const isPublished = r.status === "published";
              return (
                <tr key={r.id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`rnd-row-${r.id}`}>
                  <td className="p-3 text-white">{r.name}</td>
                  <td className="p-3 text-white">{compName(r.competition_id)}</td>
                  <td className="p-3 text-white">{fmtDate(r.main_date)}</td>
                  <td className="p-3 text-white">{r.phase}</td>
                  <td className="p-3 text-white">
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ borderColor: STATUS_COLOR[r.status] || "#27272A", color: STATUS_COLOR[r.status] || "#A1A1AA" }}>{r.status}</span>
                  </td>
                  {canAdmin && (
                    <td className="p-3 text-right whitespace-nowrap">
                      {isPublished
                        ? <button onClick={() => unpublishRound(r)} disabled={publishing === r.id} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#FF9F0A] text-[#FF9F0A] hover:bg-[#FF9F0A]/10 mr-2" title="Retirar del calendario" data-testid={`unpublish-${r.id}`}><CalendarX size={11} className="inline mr-1"/>Retirar</button>
                        : <button onClick={() => publishRound(r)} disabled={publishing === r.id} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-cyan-400 text-cyan-400 hover:bg-cyan-400/10 mr-2" title="Publicar al calendario del club" data-testid={`publish-${r.id}`}><CalendarCheck size={11} className="inline mr-1"/>Publicar</button>
                      }
                      <button onClick={() => setEditing(r)} className="text-[#A1A1AA] hover:text-white mr-2" title="Editar" data-testid={`edit-${r.id}`}><Pencil size={12}/></button>
                      <button onClick={() => removeRound(r)} className="text-[#71717A] hover:text-[#FF3B30]" title="Eliminar fecha (borra sus partidos)" data-testid={`rnd-delete-${r.id}`}><Trash2 size={12}/></button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {(editing || creating) && (
        <RoundModal category={category} comps={comps} initial={editing} onClose={() => { setEditing(null); setCreating(false); }} onSaved={() => { setEditing(null); setCreating(false); load(); }}/>
      )}
      <Pagination page={page} pages={pages} total={total} pageSize={pageSize} onPage={setPage} label="fecha(s)" testid="rnd-pagination"/>
    </div>
  );
}

function RoundModal({ comps, initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [d, setD] = useState(initial || { competition_id: comps[0]?.id || "", name: "", main_date: "", description: "", phase: "regular", counts_for_standing: true, status: "draft" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/fixture/rounds/${initial.id}`, d);
      else await api.post("/fixture/rounds", d);
      toast.success("Guardado"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title={isEdit ? "Editar fecha" : "Nueva fecha"} onClose={onClose}>
      {!isEdit && (
        <Field label="Competencia *">
          <select required className="input-tactical" value={d.competition_id} onChange={e => setD({ ...d, competition_id: e.target.value })} data-testid="rnd-comp">
            {comps.map(c => <option key={c.id} value={c.id}>{c.name} · {c.season}</option>)}
          </select>
        </Field>
      )}
      <Field label="Nombre *"><input required className="input-tactical" placeholder="Fecha 1 / Semifinal / Amistoso" value={d.name} onChange={e => setD({ ...d, name: e.target.value })} data-testid="rnd-name"/></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Fecha principal"><input type="date" className="input-tactical" value={d.main_date || ""} onChange={e => setD({ ...d, main_date: e.target.value })}/></Field>
        <Field label="Fase">
          <select className="input-tactical" value={d.phase} onChange={e => setD({ ...d, phase: e.target.value })}>
            <option value="regular">Temporada regular</option><option value="playoffs">Playoffs</option><option value="semifinal">Semifinal</option><option value="final">Final</option><option value="friendly">Amistoso</option>
          </select>
        </Field>
      </div>
      <Field label="Descripción"><input className="input-tactical" value={d.description || ""} onChange={e => setD({ ...d, description: e.target.value })}/></Field>
      <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={d.counts_for_standing} onChange={e => setD({ ...d, counts_for_standing: e.target.checked })}/><span className="text-sm text-white">Los partidos de esta fecha cuentan para el standing</span></label>
      {isEdit && (
        <Field label="Estado">
          <select className="input-tactical" value={d.status} onChange={e => setD({ ...d, status: e.target.value })}>
            <option value="draft">Borrador</option><option value="published">Publicada</option><option value="finished">Finalizada</option><option value="postponed">Postergada</option><option value="cancelled">Cancelada</option>
          </select>
        </Field>
      )}
      <ModalFooter onClose={onClose} onSave={save} saving={saving}/>
    </ModalShell>
  );
}

// ---------- Partidos ----------
function MatchesPanel({ category, canAdmin }) {
  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = async () => {
    const [m, t, r] = await Promise.all([
      api.get(`/fixture/matches?category=${category}`),
      api.get(`/fixture/teams?category=${category}&active=true`),
      api.get(`/fixture/rounds?category=${category}`),
    ]);
    setItems(m.data); setTeams(t.data); setRounds(r.data);
  };
  useEffect(() => { load().catch(() => toast.error("Error")); }, [category]); // eslint-disable-line
  const removeMatch = async (m) => {
    const label = (m.home_team_id && m.away_team_id) ? "este partido" : "este partido";
    if (!window.confirm(`Eliminar ${label}? Si estaba publicado, también se borrará su evento del calendario del club.`)) return;
    try { await api.delete(`/fixture/matches/${m.id}`); toast.success("Partido eliminado"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };
  const teamName = (id) => teams.find(t => t.id === id)?.name || teams.find(t => t.id === id)?.short_name || "—";
  const roundName = (id) => rounds.find(r => r.id === id)?.name || "—";
  const pageSize = 20;
  const { page, setPage, pages, total, slice } = usePaginated(items, pageSize, category);
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs uppercase tracking-widest text-[#A1A1AA]">{items.length} partido(s)</div>
        {canAdmin && <button onClick={() => setCreating(true)} className="btn-primary text-xs flex items-center gap-1" disabled={rounds.length === 0 || teams.length < 2} data-testid="new-match"><Plus size={12}/> Nuevo partido</button>}
      </div>
      {/* Vista tabla (md+) */}
      <div className="hidden md:block">
        <SimpleTable
          cols={[
            { k: "round_id", l: "Fecha", fmt: roundName },
            { k: "home_team_id", l: "Local", fmt: teamName },
            { k: "away_team_id", l: "Visita", fmt: teamName },
            { k: "date", l: "Día", fmt: fmtDate },
            { k: "time", l: "Hora" },
            { k: "location", l: "Lugar" },
            { k: "status", l: "Estado", badge: true },
            { k: "home_score", l: "Marcador", fmt: (_, r) => (r.home_score != null && r.away_score != null) ? `${r.home_score} – ${r.away_score}` : "—" },
          ]}
          rows={slice} canAdmin={canAdmin} onEdit={setEditing} onDelete={removeMatch} deleteLabel="Eliminar" testidPrefix="mtc"/>
      </div>
      {/* Vista tarjetas (móvil) */}
      <div className="md:hidden space-y-2">
        {items.length === 0 && <div className="tactical-card p-6 text-center text-[#71717A] text-sm">Sin partidos.</div>}
        {slice.map(m => {
          const hasScore = m.home_score != null && m.away_score != null;
          return (
            <div key={m.id} className="tactical-card p-4" data-testid={`mtc-card-${m.id}`}>
              <div className="flex justify-between items-start mb-2">
                <div className="overline text-[#71717A] text-[10px]">{roundName(m.round_id)}</div>
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ borderColor: STATUS_COLOR[m.status] || "#27272A", color: STATUS_COLOR[m.status] || "#A1A1AA" }}>{m.status}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-semibold truncate">{teamName(m.home_team_id)}</div>
                  <div className="text-xs text-[#71717A]">Local</div>
                </div>
                <div className="text-lg font-mono text-white flex-shrink-0 px-3">
                  {hasScore ? `${m.home_score} – ${m.away_score}` : "vs"}
                </div>
                <div className="flex-1 min-w-0 text-right">
                  <div className="text-sm text-white font-semibold truncate">{teamName(m.away_team_id)}</div>
                  <div className="text-xs text-[#71717A]">Visita</div>
                </div>
              </div>
              <div className="flex justify-between mt-3 pt-3 border-t border-[#27272A] text-xs text-[#A1A1AA]">
                <span>{fmtDate(m.date)}{m.time ? ` · ${m.time}` : ""}</span>
                <span className="truncate max-w-[50%] text-right">{m.location || "—"}</span>
              </div>
              {canAdmin && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setEditing(m)} className="flex-1 text-[10px] uppercase tracking-wider px-2 py-1.5 border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-cyan-400 flex items-center justify-center gap-1" data-testid={`mtc-edit-${m.id}`}>
                    <Pencil size={11}/> Editar
                  </button>
                  <button onClick={() => removeMatch(m)} className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#27272A] text-[#71717A] hover:text-[#FF3B30] hover:border-[#FF3B30] flex items-center justify-center gap-1" data-testid={`mtc-delete-${m.id}`}>
                    <Trash2 size={11}/>
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Pagination page={page} pages={pages} total={total} pageSize={pageSize} onPage={setPage} label="partido(s)" testid="mtc-pagination"/>
      {(editing || creating) && (
        <MatchModal teams={teams} rounds={rounds} initial={editing} onClose={() => { setEditing(null); setCreating(false); }} onSaved={() => { setEditing(null); setCreating(false); load(); }}/>
      )}
    </div>
  );
}

function MatchModal({ teams, rounds, initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [d, setD] = useState(initial || { round_id: rounds[0]?.id || "", home_team_id: "", away_team_id: "", date: "", time: "", end_time: "", location: "", address: "", neutral_venue: false, counts_for_standing: true, notes: "" });
  const [saving, setSaving] = useState(false);
  const [mvpCandidates, setMvpCandidates] = useState([]);

  // Cargar candidatos a MVP al editar (solo modo edición y admin).
  useEffect(() => {
    if (!initial?.id) return;
    api.get(`/fixture/matches/${initial.id}/mvp_candidates`)
      .then(r => setMvpCandidates(r.data))
      .catch(() => setMvpCandidates([]));
  }, [initial?.id]);

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/fixture/matches/${initial.id}`, d);
      else await api.post("/fixture/matches", d);
      toast.success("Guardado"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title={isEdit ? "Editar partido" : "Nuevo partido"} onClose={onClose}>
      {!isEdit && (
        <Field label="Fecha (jornada) *">
          <select required className="input-tactical" value={d.round_id} onChange={e => setD({ ...d, round_id: e.target.value })}>
            {rounds.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Equipo local *"><select required className="input-tactical" value={d.home_team_id} onChange={e => setD({ ...d, home_team_id: e.target.value })}><option value="">—</option>{teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>
        <Field label="Equipo visita *"><select required className="input-tactical" value={d.away_team_id} onChange={e => setD({ ...d, away_team_id: e.target.value })}><option value="">—</option>{teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>
        <Field label="Día *"><input required type="date" className="input-tactical" value={d.date} onChange={e => setD({ ...d, date: e.target.value })}/></Field>
        <Field label="Hora"><input type="time" className="input-tactical" value={d.time || ""} onChange={e => setD({ ...d, time: e.target.value })}/></Field>
        <Field label="Lugar"><input className="input-tactical" value={d.location || ""} onChange={e => setD({ ...d, location: e.target.value })}/></Field>
        <Field label="Dirección"><input className="input-tactical" value={d.address || ""} onChange={e => setD({ ...d, address: e.target.value })}/></Field>
      </div>
      {isEdit && (
        <>
          <div className="border-t border-[#27272A] pt-3 mt-2">
            <div className="overline text-cyan-400 mb-2">Resultado</div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Local"><input type="number" min={0} className="input-tactical" value={d.home_score ?? ""} onChange={e => setD({ ...d, home_score: e.target.value === "" ? null : Number(e.target.value) })}/></Field>
              <Field label="Visita"><input type="number" min={0} className="input-tactical" value={d.away_score ?? ""} onChange={e => setD({ ...d, away_score: e.target.value === "" ? null : Number(e.target.value) })}/></Field>
              <Field label="Estado">
                <select className="input-tactical" value={d.status || "scheduled"} onChange={e => setD({ ...d, status: e.target.value })}>
                  <option value="scheduled">Programado</option><option value="published">Publicado</option><option value="live">En juego</option><option value="finished">Finalizado</option><option value="postponed">Postergado</option><option value="cancelled">Cancelado</option>
                </select>
              </Field>
            </div>
          </div>
          <div className="border-t border-[#27272A] pt-3 mt-2">
            <div className="overline text-cyan-400 mb-2 flex items-center gap-1"><Star size={12}/> Jugador destacado (MVP)</div>
            <Field label="Selecciona la estrella del partido">
              <select className="input-tactical" value={d.mvp_user_id || ""} onChange={e => setD({ ...d, mvp_user_id: e.target.value || null })} data-testid="mvp-select">
                <option value="">— Sin destacado —</option>
                {mvpCandidates.map(p => (
                  <option key={p.user_id} value={p.user_id}>
                    {p.jersey_number != null ? `#${p.jersey_number} ` : ""}{p.name}{p.position ? ` · ${p.position}` : ""}
                  </option>
                ))}
              </select>
            </Field>
            {d.mvp_user_id && (
              <Field label="Comentario del coach (opcional)">
                <textarea rows={2} maxLength={280} className="input-tactical" placeholder="Ej: 3 touchdowns y liderazgo defensivo en el 4to cuarto." value={d.mvp_note || ""} onChange={e => setD({ ...d, mvp_note: e.target.value })} data-testid="mvp-note"/>
              </Field>
            )}
            <div className="text-[10px] text-[#71717A] mt-1">
              El MVP aparecerá con foto y nombre en el post automático de Somos Ferro cuando marques el partido como <strong>Finalizado</strong>.
            </div>
          </div>
        </>
      )}
      <ModalFooter onClose={onClose} onSave={save} saving={saving}/>
    </ModalShell>
  );
}

// ---------- helpers UI ----------
function SimpleTable({ cols, rows, canAdmin, onEdit, onDelete, onRowClick, deleteLabel = "Eliminar", testidPrefix = "row" }) {
  return (
    <div className="tactical-card overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[#27272A] text-left">{cols.map(c => <th key={c.k} className="p-3 overline">{c.l}</th>)}{canAdmin && <th className="p-3 overline text-right">Acciones</th>}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length + 1} className="p-10 text-center text-[#71717A]">Sin datos.</td></tr>}
          {rows.map(r => (
            <tr key={r.id} className={`border-b border-[#27272A] hover:bg-[#121212] ${onRowClick ? "cursor-pointer" : ""}`} data-testid={`${testidPrefix}-row-${r.id}`}>
              {cols.map(c => (
                <td key={c.k} className="p-3 text-white" onClick={onRowClick ? () => onRowClick(r) : undefined}>
                  {c.badge
                    ? <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ borderColor: STATUS_COLOR[r[c.k]] || "#27272A", color: STATUS_COLOR[r[c.k]] || "#A1A1AA" }}>{r[c.k]}</span>
                    : (c.fmt ? c.fmt(r[c.k], r) : (r[c.k] ?? "—"))}
                </td>
              ))}
              {canAdmin && (
                <td className="p-3 text-right">
                  <button onClick={() => onEdit(r)} className="text-[#A1A1AA] hover:text-white mr-2" data-testid={`edit-${r.id}`}><Pencil size={12}/></button>
                  {onDelete && <button onClick={() => onDelete(r)} className="text-[#71717A] hover:text-[#FF3B30]" title={deleteLabel} data-testid={`del-${r.id}`}><Trash2 size={12}/></button>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="tactical-card p-6 w-full max-w-lg bg-[#0A0A0A] my-4 md:my-0 space-y-3" onClick={e => e.stopPropagation()} data-testid="fixture-modal">
        <h3 className="font-display text-xl text-white">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="overline block mb-1">{label}</span>{children}</label>;
}

function ModalFooter({ onClose, onSave, saving }) {
  return (
    <div className="flex justify-end gap-2 pt-3 border-t border-[#27272A]">
      <button onClick={onClose} disabled={saving} className="btn-ghost text-xs">Cancelar</button>
      <button onClick={onSave} disabled={saving} className="btn-primary text-xs" data-testid="modal-save">{saving ? "Guardando…" : "Guardar"}</button>
    </div>
  );
}

// ---------- Paginación cliente ----------
// resetKey: cambia (p.ej. category / competition) para volver a page 1 automáticamente.
function usePaginated(items, pageSize, resetKey) {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [resetKey, pageSize]);
  const total = items.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pages);
  const slice = items.slice((safePage - 1) * pageSize, safePage * pageSize);
  return { page: safePage, setPage, pages, total, slice };
}

function Pagination({ page, pages, total, pageSize, onPage, label = "resultado(s)", testid = "pagination" }) {
  if (total <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-3 py-2 px-3 border border-[#27272A] tactical-card" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#71717A]">
        {from}–{to} de {total} {label}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => onPage(page - 1)} disabled={page <= 1} className="text-xs px-2 py-1 border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-cyan-400 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`${testid}-prev`}>‹ Anterior</button>
        <span className="text-xs text-white font-mono min-w-[70px] text-center">{page} / {pages}</span>
        <button onClick={() => onPage(page + 1)} disabled={page >= pages} className="text-xs px-2 py-1 border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-cyan-400 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`${testid}-next`}>Siguiente ›</button>
      </div>
    </div>
  );
}


// ---------- Tabla de posiciones ----------
function StandingsPanel({ category }) {
  const [comps, setComps] = useState([]);
  const [selected, setSelected] = useState("");
  const [standings, setStandings] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/fixture/competitions?category=${category}`)
      .then(r => {
        const active = r.data.filter(c => ["active", "finished", "draft"].includes(c.status));
        setComps(active);
        const preferred = active.find(c => c.status === "active") || active[0];
        setSelected(preferred?.id || "");
      })
      .catch(() => toast.error("Error"));
  }, [category]);

  useEffect(() => {
    if (!selected) { setStandings([]); setMeta(null); return; }
    setLoading(true);
    api.get(`/fixture/competitions/${selected}/standings`)
      .then(r => {
        setStandings(r.data.standings);
        setMeta({ competition: r.data.competition, matches: r.data.matches_counted });
      })
      .catch(() => toast.error("Error cargando la tabla"))
      .finally(() => setLoading(false));
  }, [selected]);

  const pageSize = 20;
  const { page, setPage, pages, total, slice } = usePaginated(standings, pageSize, selected);

  return (
    <div data-testid="standings-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-xs uppercase tracking-widest text-[#A1A1AA]">Temporada</div>
        <select value={selected} onChange={e => setSelected(e.target.value)} className="input-tactical text-sm w-auto min-w-[220px]" data-testid="standings-comp-select">
          {comps.length === 0 && <option value="">Sin competencias</option>}
          {comps.map(c => <option key={c.id} value={c.id}>{c.name} · {c.season}</option>)}
        </select>
        {meta && <div className="text-xs text-[#71717A]">{meta.matches} partido(s) contabilizado(s)</div>}
      </div>

      {!selected && <div className="text-center text-[#71717A] py-16">Crea una competencia y registra partidos finalizados para ver la tabla.</div>}
      {selected && (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#27272A] text-left">
                <th className="p-2 md:p-3 overline text-[#A1A1AA]">Pos</th>
                <th className="p-2 md:p-3 overline text-[#A1A1AA]">Equipo</th>
                <th className="p-2 md:p-3 overline text-center" title="Partidos Ganados">PG</th>
                <th className="p-2 md:p-3 overline text-center" title="Partidos Perdidos">PP</th>
                <th className="p-2 md:p-3 overline text-center" title="Partidos Empatados">PE</th>
                <th className="p-2 md:p-3 overline text-center hidden sm:table-cell" title="Porcentaje de partidos ganados">%</th>
                <th className="p-2 md:p-3 overline text-center hidden md:table-cell" title="Puntos a favor">PF</th>
                <th className="p-2 md:p-3 overline text-center hidden md:table-cell" title="Puntos en contra">PC</th>
                <th className="p-2 md:p-3 overline text-center" title="Diferencia (PF−PC)">DIF</th>
                <th className="p-2 md:p-3 overline text-center hidden lg:table-cell" title="Récord de local">Loc.</th>
                <th className="p-2 md:p-3 overline text-center hidden lg:table-cell" title="Récord de visita">Vis.</th>
                <th className="p-2 md:p-3 overline text-center hidden sm:table-cell" title="Racha actual">Rach</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={12} className="p-10 text-center text-[#71717A]">Cargando…</td></tr>}
              {!loading && standings.length === 0 && <tr><td colSpan={12} className="p-10 text-center text-[#71717A]">Aún no hay partidos finalizados en esta competencia.</td></tr>}
              {!loading && slice.map(row => (
                <tr key={row.team_id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`standings-row-${row.team_id}`}>
                  <td className="p-2 md:p-3 text-white font-bold">{row.position}</td>
                  <td className="p-2 md:p-3 text-white text-xs md:text-sm">{row.team_name}</td>
                  <td className="p-2 md:p-3 text-center text-white">{row.wins}</td>
                  <td className="p-2 md:p-3 text-center text-white">{row.losses}</td>
                  <td className="p-2 md:p-3 text-center text-white">{row.ties}</td>
                  <td className="p-2 md:p-3 text-center text-white font-mono text-xs md:text-sm hidden sm:table-cell">{fmtPct(row.win_pct)}</td>
                  <td className="p-2 md:p-3 text-center text-white hidden md:table-cell">{row.points_for}</td>
                  <td className="p-2 md:p-3 text-center text-white hidden md:table-cell">{row.points_against}</td>
                  <td className={`p-2 md:p-3 text-center font-mono font-semibold ${row.point_diff > 0 ? "text-[#34C759]" : row.point_diff < 0 ? "text-[#FF3B30]" : "text-white"}`}>{row.point_diff > 0 ? "+" : ""}{row.point_diff}</td>
                  <td className="p-2 md:p-3 text-center text-[#A1A1AA] font-mono hidden lg:table-cell">{row.home_record}</td>
                  <td className="p-2 md:p-3 text-center text-[#A1A1AA] font-mono hidden lg:table-cell">{row.away_record}</td>
                  <td className="p-2 md:p-3 text-center hidden sm:table-cell">
                    <span className={`font-mono text-xs px-1.5 py-0.5 border ${
                      row.streak.startsWith("G") ? "border-[#34C759] text-[#34C759]"
                        : row.streak.startsWith("P") ? "border-[#FF3B30] text-[#FF3B30]"
                        : row.streak.startsWith("E") ? "border-[#FF9F0A] text-[#FF9F0A]"
                        : "border-[#27272A] text-[#71717A]"
                    }`}>{row.streak}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {meta && (
            <div className="px-3 py-2 border-t border-[#27272A] text-[10px] uppercase tracking-widest text-[#71717A]">
              Orden: PG → % → DIF → PF
            </div>
          )}
        </div>
      )}
      {selected && !loading && <Pagination page={page} pages={pages} total={total} pageSize={pageSize} onPage={setPage} label="equipo(s)" testid="standings-pagination"/>}
    </div>
  );
}


// ---------- Rivales (histórico Ferro vs cada rival) ----------
function RivalsPanel({ category }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // rival object for modal
  useEffect(() => {
    setLoading(true);
    api.get(`/fixture/head_to_head?category=${category}`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Error cargando rivales"))
      .finally(() => setLoading(false));
  }, [category]);

  const pageSize = 12;
  const { page, setPage, pages, total, slice } = usePaginated(data?.rivals || [], pageSize, category);

  if (loading) return <div className="text-center text-[#71717A] py-16">Cargando historial…</div>;
  if (!data?.ferro_team) return <div className="text-center text-[#71717A] py-16">Aún no hay equipo &ldquo;Ferroviarios&rdquo; configurado en <b>{CAT_LABEL[category]}</b>.<br/>Créalo desde la sub-tab Equipos y márcalo como <em>Ferroviarios</em>.</div>;
  const rivals = data.rivals || [];
  return (
    <div data-testid="rivals-panel">
      <div className="flex flex-wrap items-baseline gap-3 mb-4">
        <div className="text-xs uppercase tracking-widest text-[#A1A1AA]">Historial de {data.ferro_team.name}</div>
        <div className="text-xs text-[#71717A]">{rivals.length} rival(es) · {data.total_matches} partido(s)</div>
      </div>
      {rivals.length === 0 && <div className="text-center text-[#71717A] py-16 text-sm">Aún no hay partidos finalizados registrados contra rivales.<br/>Al finalizar un partido oficial, aparecerá aquí el historial contra ese rival.</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {slice.map(r => (
          <button key={r.rival_team_id} onClick={() => setDetail(r)} className="tactical-card p-4 text-left hover:border-cyan-400 transition-colors" data-testid={`rival-card-${r.rival_team_id}`}>
            <div className="flex justify-between items-start mb-3">
              <div className="min-w-0">
                <div className="text-white font-semibold truncate">{r.rival_team_name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#71717A] mt-0.5">{r.played} partido(s) · {fmtPct(r.win_pct)}</div>
              </div>
              <ChevronRight size={16} className="text-[#71717A] flex-shrink-0"/>
            </div>
            <div className="flex gap-2 text-center">
              <div className="flex-1 border border-[#27272A] py-1.5">
                <div className="text-lg font-bold" style={{ color: RESULT_COLOR.W }}>{r.wins}</div>
                <div className="text-[9px] uppercase tracking-widest text-[#71717A]">Gan</div>
              </div>
              <div className="flex-1 border border-[#27272A] py-1.5">
                <div className="text-lg font-bold" style={{ color: RESULT_COLOR.T }}>{r.ties}</div>
                <div className="text-[9px] uppercase tracking-widest text-[#71717A]">Emp</div>
              </div>
              <div className="flex-1 border border-[#27272A] py-1.5">
                <div className="text-lg font-bold" style={{ color: RESULT_COLOR.L }}>{r.losses}</div>
                <div className="text-[9px] uppercase tracking-widest text-[#71717A]">Per</div>
              </div>
            </div>
            <div className="flex justify-between mt-3 text-xs text-[#A1A1AA]">
              <span>PF <span className="text-white font-mono">{r.points_for}</span></span>
              <span>PC <span className="text-white font-mono">{r.points_against}</span></span>
              <span>DIF <span className={`font-mono ${r.point_diff > 0 ? "text-[#34C759]" : r.point_diff < 0 ? "text-[#FF3B30]" : "text-[#A1A1AA]"}`}>{r.point_diff > 0 ? "+" : ""}{r.point_diff}</span></span>
            </div>
            {r.last_5.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#27272A] flex items-center gap-1.5">
                <span className="text-[9px] uppercase tracking-widest text-[#71717A] mr-1">Últ. {r.last_5.length}</span>
                {r.last_5.map((res, i) => (
                  <span key={i} className="w-6 h-6 flex items-center justify-center text-[10px] font-bold border" style={{ borderColor: RESULT_COLOR[res], color: RESULT_COLOR[res] }}>
                    {RESULT_ES[res]}
                  </span>
                ))}
              </div>
            )}
          </button>
        ))}
      </div>
      {detail && <RivalDetailModal rival={detail} ferroName={data.ferro_team.name} onClose={() => setDetail(null)}/>}
      <Pagination page={page} pages={pages} total={total} pageSize={pageSize} onPage={setPage} label="rival(es)" testid="rivals-pagination"/>
    </div>
  );
}

function RivalDetailModal({ rival, ferroName, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="tactical-card w-full max-w-2xl bg-[#0A0A0A] my-4 md:my-0" onClick={e => e.stopPropagation()} data-testid="rival-detail-modal">
        <div className="p-6 border-b border-[#27272A] flex justify-between items-start">
          <div>
            <div className="overline text-cyan-400 mb-1">Historial</div>
            <div className="font-display text-2xl text-white">{ferroName} <span className="text-[#71717A]">vs</span> {rival.rival_team_name}</div>
            <div className="text-xs text-[#71717A] mt-1">{rival.played} partido(s) · {rival.wins}G / {rival.ties}E / {rival.losses}P · {fmtPct(rival.win_pct)}</div>
          </div>
          <button onClick={onClose} className="text-[#71717A] hover:text-white flex-shrink-0" data-testid="rival-detail-close"><X size={20}/></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <StatBox label="Puntos a favor" value={rival.points_for}/>
            <StatBox label="Puntos en contra" value={rival.points_against}/>
            <StatBox label="Diferencia" value={`${rival.point_diff > 0 ? "+" : ""}${rival.point_diff}`} accent={rival.point_diff > 0 ? "#34C759" : rival.point_diff < 0 ? "#FF3B30" : "#A1A1AA"}/>
          </div>
          {(rival.biggest_win || rival.biggest_loss) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {rival.biggest_win && <MilestoneBox title="Mayor victoria" record={rival.biggest_win} color="#34C759" ferroName={ferroName} rivalName={rival.rival_team_name}/>}
              {rival.biggest_loss && <MilestoneBox title="Peor derrota" record={rival.biggest_loss} color="#FF3B30" ferroName={ferroName} rivalName={rival.rival_team_name}/>}
            </div>
          )}
          <div>
            <div className="overline text-cyan-400 mb-2">Todos los partidos</div>
            <div className="space-y-1">
              {rival.matches.map(m => (
                <div key={m.match_id} className="flex items-center justify-between py-2 px-3 border border-[#27272A] hover:bg-[#121212] text-sm" data-testid={`rival-match-${m.match_id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="w-6 h-6 flex items-center justify-center text-[10px] font-bold border flex-shrink-0" style={{ borderColor: RESULT_COLOR[m.result], color: RESULT_COLOR[m.result] }}>{RESULT_ES[m.result]}</span>
                    <div className="min-w-0">
                      <div className="text-white text-xs md:text-sm truncate">{m.ferro_was_home ? `${ferroName} vs ${rival.rival_team_name}` : `${rival.rival_team_name} vs ${ferroName}`}</div>
                      <div className="text-[10px] text-[#71717A] truncate">{fmtDate(m.date)}{m.competition_name ? ` · ${m.competition_name}${m.season ? " · " + m.season : ""}` : ""}</div>
                    </div>
                  </div>
                  <div className="font-mono text-white text-sm flex-shrink-0 ml-2">{m.score_ferro}-{m.score_rival}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className="tactical-card p-3 text-center">
      <div className="text-[9px] uppercase tracking-widest text-[#71717A]">{label}</div>
      <div className="text-xl font-bold mt-1" style={{ color: accent || "#fff" }}>{value}</div>
    </div>
  );
}

function MilestoneBox({ title, record, color, ferroName, rivalName }) {
  return (
    <div className="tactical-card p-3">
      <div className="overline mb-1" style={{ color }}>{title}</div>
      <div className="text-white font-mono text-lg">{record.score_ferro} – {record.score_rival}</div>
      <div className="text-[10px] text-[#A1A1AA] mt-0.5">{record.ferro_was_home ? "de local" : "de visita"} · {fmtDate(record.date)}</div>
      {record.competition_name && <div className="text-[10px] text-[#71717A] mt-0.5 truncate">{record.competition_name}{record.season ? ` · ${record.season}` : ""}{record.round_name ? ` · ${record.round_name}` : ""}</div>}
    </div>
  );
}

// ============================================================
// COMPONENTES DE LA VISTA POR COMPETENCIA (jerarquía Fase 5)
// ============================================================

function CompetitionSummary({ comp }) {
  const [standings, setStandings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [rounds, setRounds] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get(`/fixture/competitions/${comp.id}/standings`).then(r => setStandings(r.data.standings || [])).catch(() => {}),
      api.get(`/fixture/matches?competition_id=${comp.id}`).then(r => setMatches(r.data)).catch(() => {}),
      api.get(`/fixture/competitions/${comp.id}/teams`).then(r => setTeams(r.data)).catch(() => {}),
      api.get(`/fixture/rounds?competition_id=${comp.id}`).then(r => setRounds(r.data)).catch(() => {}),
    ]);
  }, [comp.id]);
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = matches.filter(m => m.status !== "finished" && m.date >= today).sort((a, b) => a.date.localeCompare(b.date))[0];
  const lastResult = matches.filter(m => m.status === "finished").sort((a, b) => b.date.localeCompare(a.date))[0];
  const leader = standings[0];
  const teamName = (id) => (teams.find(t => t.team_id === id) || {}).team?.name || "—";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="comp-summary">
      <StatBox label="Equipos inscritos" value={teams.length}/>
      <StatBox label="Fechas / Jornadas" value={rounds.length}/>
      <StatBox label="Partidos totales" value={matches.length}/>
      <StatBox label="Finalizados" value={matches.filter(m => m.status === "finished").length}/>
      <div className="tactical-card p-4 md:col-span-2">
        <div className="overline text-cyan-400 mb-2">Próximo partido</div>
        {upcoming ? (
          <div>
            <div className="text-white font-semibold">{teamName(upcoming.home_team_id)} <span className="text-[#71717A]">vs</span> {teamName(upcoming.away_team_id)}</div>
            <div className="text-xs text-[#A1A1AA] mt-1">{fmtDate(upcoming.date)}{upcoming.time ? ` · ${upcoming.time}` : ""}{upcoming.location ? ` · ${upcoming.location}` : ""}</div>
          </div>
        ) : <div className="text-[#71717A] text-sm">Sin partidos programados.</div>}
      </div>
      <div className="tactical-card p-4 md:col-span-2">
        <div className="overline text-cyan-400 mb-2">Último resultado</div>
        {lastResult ? (
          <div>
            <div className="text-white font-semibold">{teamName(lastResult.home_team_id)} <span className="font-mono text-cyan-400 mx-2">{lastResult.home_score}–{lastResult.away_score}</span> {teamName(lastResult.away_team_id)}</div>
            <div className="text-xs text-[#A1A1AA] mt-1">{fmtDate(lastResult.date)}</div>
          </div>
        ) : <div className="text-[#71717A] text-sm">Sin partidos finalizados.</div>}
      </div>
      {leader && (
        <div className="tactical-card p-4 md:col-span-4">
          <div className="overline text-cyan-400 mb-2">Líder de la tabla</div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="text-white font-bold text-lg">{leader.team_name}</div>
            <div className="text-xs text-[#A1A1AA]">{leader.wins}G · {leader.ties}E · {leader.losses}P · DIF {leader.point_diff > 0 ? "+" : ""}{leader.point_diff}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function EnrolledTeamsPanel({ comp, canAdmin }) {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const [selectedTid, setSelectedTid] = useState("");
  const load = () => Promise.all([
    api.get(`/fixture/competitions/${comp.id}/teams`).then(r => setItems(r.data)),
    api.get(`/fixture/teams?category=${comp.category}&active=true`).then(r => setCatalog(r.data)),
  ]);
  useEffect(() => { load().catch(() => toast.error("Error")); }, [comp.id]); // eslint-disable-line

  const enrolledIds = new Set(items.map(i => i.team_id));
  const available = catalog.filter(t => !enrolledIds.has(t.id));

  const enroll = async () => {
    if (!selectedTid) return;
    try { await api.post(`/fixture/competitions/${comp.id}/teams`, { team_id: selectedTid }); toast.success("Equipo inscrito"); setSelectedTid(""); setEnrolling(false); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };
  const unenroll = async (row) => {
    if (!window.confirm(`Desinscribir a "${row.team?.name}" de esta competencia?`)) return;
    try { await api.delete(`/fixture/competitions/${comp.id}/teams/${row.team_id}`); toast.success("Equipo desinscrito"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };
  return (
    <div data-testid="enrolled-panel">
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs uppercase tracking-widest text-[#A1A1AA]">{items.length} equipo(s) inscrito(s)</div>
        {canAdmin && !enrolling && <button onClick={() => setEnrolling(true)} className="btn-primary text-xs flex items-center gap-1" data-testid="enroll-team-btn"><Plus size={12}/> Inscribir equipo</button>}
      </div>
      {enrolling && (
        <div className="tactical-card p-3 mb-3 flex flex-col md:flex-row items-stretch md:items-end gap-2" data-testid="enroll-team-form">
          <div className="flex-1">
            <div className="overline text-[10px] text-[#A1A1AA] mb-1">Elige del catálogo global de {CAT_LABEL[comp.category]}</div>
            <select className="input-tactical w-full" value={selectedTid} onChange={e => setSelectedTid(e.target.value)}>
              <option value="">— Selecciona un equipo —</option>
              {available.map(t => <option key={t.id} value={t.id}>{t.is_ferroviarios ? "★ " : ""}{t.name}{t.short_name ? ` (${t.short_name})` : ""}</option>)}
            </select>
          </div>
          <button onClick={enroll} disabled={!selectedTid} className="btn-primary text-xs whitespace-nowrap">Inscribir</button>
          <button onClick={() => { setEnrolling(false); setSelectedTid(""); }} className="btn-ghost text-xs whitespace-nowrap">Cancelar</button>
        </div>
      )}
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#27272A] text-left">
            <th className="p-3 overline">Equipo</th>
            <th className="p-3 overline hidden md:table-cell">Sigla</th>
            <th className="p-3 overline hidden md:table-cell">Ciudad</th>
            <th className="p-3 overline text-center">Ferro</th>
            {canAdmin && <th className="p-3 overline text-right">Acciones</th>}
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={canAdmin ? 5 : 4} className="p-10 text-center text-[#71717A]">Sin equipos inscritos. Usa <b>Inscribir equipo</b> para agregarlos.</td></tr>}
            {items.map(row => (
              <tr key={row.id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`enrolled-row-${row.team_id}`}>
                <td className="p-3 text-white">{row.team?.name || "—"}{row.auto_backfilled && <span className="ml-2 text-[9px] uppercase text-[#71717A]" title="Inscripción derivada automáticamente de partidos previos">auto</span>}</td>
                <td className="p-3 text-[#A1A1AA] hidden md:table-cell">{row.team?.short_name || "—"}</td>
                <td className="p-3 text-[#A1A1AA] hidden md:table-cell">{row.team?.city || "—"}</td>
                <td className="p-3 text-center">{row.team?.is_ferroviarios ? <Star size={14} className="inline text-[#FACC15]"/> : ""}</td>
                {canAdmin && <td className="p-3 text-right"><button onClick={() => unenroll(row)} className="text-[#71717A] hover:text-[#FF3B30]" title="Desinscribir" data-testid={`unenroll-${row.team_id}`}><Trash2 size={12}/></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CalendarByRoundsPanel({ comp, canAdmin }) {
  const [rounds, setRounds] = useState([]);
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [filterTeam, setFilterTeam] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [creatingMatchInRound, setCreatingMatchInRound] = useState(null);
  const [editingMatch, setEditingMatch] = useState(null);
  const [creatingRound, setCreatingRound] = useState(false);
  const [editingRound, setEditingRound] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const load = async () => {
    const [r, m, t] = await Promise.all([
      api.get(`/fixture/rounds?competition_id=${comp.id}`),
      api.get(`/fixture/matches?competition_id=${comp.id}`),
      api.get(`/fixture/competitions/${comp.id}/teams`),
    ]);
    setRounds(r.data.sort((a, b) => (a.main_date || "").localeCompare(b.main_date || "")));
    setMatches(m.data);
    setTeams(t.data);
  };
  useEffect(() => { load().catch(() => toast.error("Error")); }, [comp.id]); // eslint-disable-line

  const teamName = (id) => (teams.find(x => x.team_id === id) || {}).team?.name || "—";
  const toggle = (rid) => setExpanded(prev => { const n = new Set(prev); n.has(rid) ? n.delete(rid) : n.add(rid); return n; });

  const filteredMatchesForRound = (rid) => {
    let ms = matches.filter(m => m.round_id === rid);
    if (filterTeam) ms = ms.filter(m => m.home_team_id === filterTeam || m.away_team_id === filterTeam);
    if (filterStatus) ms = ms.filter(m => m.status === filterStatus);
    return ms.sort((a, b) => `${a.date}${a.time || ""}`.localeCompare(`${b.date}${b.time || ""}`));
  };
  const roundMatchesCount = (rid) => matches.filter(m => m.round_id === rid).length;

  const publishRound = async (rnd) => {
    if (!window.confirm(`Publicar "${rnd.name}" al calendario del club?`)) return;
    setPublishing(rnd.id);
    try { const r = await api.post(`/fixture/rounds/${rnd.id}/publish_to_calendar`); toast.success(`${r.data.matches_synced} partido(s) publicado(s)`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); } finally { setPublishing(null); }
  };
  const unpublishRound = async (rnd) => {
    if (!window.confirm(`Retirar "${rnd.name}" del calendario del club?`)) return;
    setPublishing(rnd.id);
    try { const r = await api.post(`/fixture/rounds/${rnd.id}/unpublish_from_calendar`); toast.success(`${r.data.matches_removed} evento(s) removido(s)`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); } finally { setPublishing(null); }
  };
  const removeRound = async (rnd) => {
    if (!window.confirm(`Eliminar la fecha "${rnd.name}"? Borra sus partidos y eventos.`)) return;
    try { await api.delete(`/fixture/rounds/${rnd.id}`); toast.success("Fecha eliminada"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };
  const removeMatch = async (m) => {
    if (!window.confirm("Eliminar este partido?")) return;
    try { await api.delete(`/fixture/matches/${m.id}`); toast.success("Partido eliminado"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const teamOpts = teams.map(t => ({ id: t.team_id, name: t.team?.name || "—" }));

  return (
    <div data-testid="calendar-panel">
      {/* Header + filtros */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
        <div className="text-xs uppercase tracking-widest text-[#A1A1AA]">{rounds.length} jornada(s) · {matches.length} partido(s)</div>
        {canAdmin && <button onClick={() => setCreatingRound(true)} className="btn-primary text-xs flex items-center gap-1 self-start" data-testid="new-round"><Plus size={12}/> Nueva jornada</button>}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <select value={filterTeam} onChange={e => setFilterTeam(e.target.value)} className="input-tactical text-xs flex-1" data-testid="cal-filter-team">
          <option value="">Todos los equipos</option>
          {teamOpts.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="input-tactical text-xs flex-1" data-testid="cal-filter-status">
          <option value="">Todos los estados</option>
          <option value="scheduled">Programados</option>
          <option value="published">Publicados</option>
          <option value="live">En juego</option>
          <option value="finished">Finalizados</option>
          <option value="postponed">Postergados</option>
          <option value="cancelled">Cancelados</option>
        </select>
      </div>

      {rounds.length === 0 && <div className="tactical-card p-8 text-center text-[#71717A] text-sm">Aún no hay jornadas. Crea la primera con <b>Nueva jornada</b>.</div>}

      <div className="space-y-2">
        {rounds.map(r => {
          const isOpen = expanded.has(r.id);
          const rMatches = filteredMatchesForRound(r.id);
          return (
            <div key={r.id} className="tactical-card overflow-hidden" data-testid={`round-block-${r.id}`}>
              <button onClick={() => toggle(r.id)} className="w-full p-3 md:p-4 flex items-center gap-3 hover:bg-[#121212] transition-colors text-left" data-testid={`round-toggle-${r.id}`}>
                <ChevronRight size={16} className={`text-[#A1A1AA] flex-shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}/>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold truncate">{r.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#71717A] mt-0.5">
                    {r.main_date ? fmtDate(r.main_date) : "Sin fecha principal"}
                    {r.phase ? ` · ${r.phase}` : ""}
                    {" · "}<span style={{color: STATUS_COLOR[r.status] || "#A1A1AA"}}>{r.status}</span>
                    {" · "}{roundMatchesCount(r.id)} partido(s)
                  </div>
                </div>
                {canAdmin && (
                  <div className="hidden sm:flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    {r.status === "published"
                      ? <button onClick={() => unpublishRound(r)} disabled={publishing === r.id} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#FF9F0A] text-[#FF9F0A] hover:bg-[#FF9F0A]/10" title="Retirar del calendario" data-testid={`unpublish-${r.id}`}><CalendarX size={11}/></button>
                      : <button onClick={() => publishRound(r)} disabled={publishing === r.id} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-cyan-400 text-cyan-400 hover:bg-cyan-400/10" title="Publicar al calendario" data-testid={`publish-${r.id}`}><CalendarCheck size={11}/></button>
                    }
                    <button onClick={() => setEditingRound(r)} className="text-[#A1A1AA] hover:text-white p-1" title="Editar fecha"><Pencil size={12}/></button>
                    <button onClick={() => removeRound(r)} className="text-[#71717A] hover:text-[#FF3B30] p-1" title="Eliminar fecha"><Trash2 size={12}/></button>
                  </div>
                )}
              </button>
              {isOpen && (
                <div className="border-t border-[#27272A] p-3 space-y-2 bg-[#0A0A0A]">
                  {canAdmin && (
                    <button onClick={() => setCreatingMatchInRound(r)} className="w-full text-[10px] uppercase tracking-widest text-cyan-400 border border-dashed border-cyan-400/40 hover:border-cyan-400 py-2 flex items-center justify-center gap-1" data-testid={`add-match-${r.id}`}><Plus size={11}/> Agregar partido a {r.name}</button>
                  )}
                  {rMatches.length === 0 && <div className="text-center text-[#71717A] text-xs py-4">Sin partidos en esta jornada (o filtros activos los ocultan).</div>}
                  {rMatches.map(m => {
                    const hasScore = m.home_score != null && m.away_score != null;
                    return (
                      <div key={m.id} className="border border-[#27272A] p-3 hover:bg-[#121212]" data-testid={`cal-match-${m.id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] uppercase tracking-widest text-[#71717A]">{fmtDate(m.date)}{m.time ? ` · ${m.time}` : ""}</span>
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ml-auto" style={{ borderColor: STATUS_COLOR[m.status] || "#27272A", color: STATUS_COLOR[m.status] || "#A1A1AA" }}>{m.status}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0 text-sm text-white font-semibold truncate text-right">{teamName(m.home_team_id)}</div>
                          <div className="font-mono text-lg text-white flex-shrink-0 px-3 min-w-[70px] text-center">{hasScore ? `${m.home_score}–${m.away_score}` : "vs"}</div>
                          <div className="flex-1 min-w-0 text-sm text-white font-semibold truncate">{teamName(m.away_team_id)}</div>
                        </div>
                        {m.location && <div className="text-[10px] text-[#71717A] mt-2 text-center">📍 {m.location}</div>}
                        {canAdmin && (
                          <div className="flex gap-2 mt-2 pt-2 border-t border-[#27272A]">
                            <button onClick={() => setEditingMatch(m)} className="flex-1 text-[10px] uppercase tracking-wider px-2 py-1 border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-cyan-400 flex items-center justify-center gap-1" data-testid={`cal-edit-${m.id}`}><Pencil size={10}/> Editar</button>
                            <button onClick={() => removeMatch(m)} className="text-[10px] uppercase tracking-wider px-3 py-1 border border-[#27272A] text-[#71717A] hover:text-[#FF3B30] hover:border-[#FF3B30]" title="Eliminar" data-testid={`cal-del-${m.id}`}><Trash2 size={10}/></button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(creatingRound || editingRound) && (
        <RoundModal category={comp.category} comps={[comp]} initial={editingRound || { competition_id: comp.id }} onClose={() => { setCreatingRound(false); setEditingRound(null); }} onSaved={() => { setCreatingRound(false); setEditingRound(null); load(); }}/>
      )}
      {(creatingMatchInRound || editingMatch) && (
        <MatchModal
          teams={teams.map(x => ({ id: x.team_id, name: x.team?.name || "—", short_name: x.team?.short_name }))}
          rounds={rounds}
          initial={editingMatch || (creatingMatchInRound ? { round_id: creatingMatchInRound.id, home_team_id: "", away_team_id: "", date: creatingMatchInRound.main_date || "", time: "" } : null)}
          onClose={() => { setCreatingMatchInRound(null); setEditingMatch(null); }}
          onSaved={() => { setCreatingMatchInRound(null); setEditingMatch(null); load(); }}
        />
      )}
    </div>
  );
}

function CompetitionStandings({ comp }) {
  // Wrapper que hace lo mismo que StandingsPanel pero fijando comp.id.
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/fixture/competitions/${comp.id}/standings`)
      .then(r => setRows(r.data.standings || []))
      .catch(() => toast.error("Error cargando la tabla"))
      .finally(() => setLoading(false));
  }, [comp.id]);
  return (
    <div data-testid="comp-standings">
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#27272A] text-left">
              <th className="p-2 md:p-3 overline">Pos</th>
              <th className="p-2 md:p-3 overline">Equipo</th>
              <th className="p-2 md:p-3 overline text-center">PG</th>
              <th className="p-2 md:p-3 overline text-center">PP</th>
              <th className="p-2 md:p-3 overline text-center">PE</th>
              <th className="p-2 md:p-3 overline text-center hidden sm:table-cell">%</th>
              <th className="p-2 md:p-3 overline text-center hidden md:table-cell">PF</th>
              <th className="p-2 md:p-3 overline text-center hidden md:table-cell">PC</th>
              <th className="p-2 md:p-3 overline text-center">DIF</th>
              <th className="p-2 md:p-3 overline text-center hidden sm:table-cell">Rach</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="p-10 text-center text-[#71717A]">Cargando…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={10} className="p-10 text-center text-[#71717A]">Aún no hay partidos finalizados.</td></tr>}
            {!loading && rows.map(r => (
              <tr key={r.team_id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`cstd-${r.team_id}`}>
                <td className="p-2 md:p-3 text-white font-bold">{r.position}</td>
                <td className="p-2 md:p-3 text-white text-xs md:text-sm">{r.team_name}</td>
                <td className="p-2 md:p-3 text-center text-white">{r.wins}</td>
                <td className="p-2 md:p-3 text-center text-white">{r.losses}</td>
                <td className="p-2 md:p-3 text-center text-white">{r.ties}</td>
                <td className="p-2 md:p-3 text-center text-white font-mono text-xs md:text-sm hidden sm:table-cell">{fmtPct(r.win_pct)}</td>
                <td className="p-2 md:p-3 text-center text-white hidden md:table-cell">{r.points_for}</td>
                <td className="p-2 md:p-3 text-center text-white hidden md:table-cell">{r.points_against}</td>
                <td className={`p-2 md:p-3 text-center font-mono font-semibold ${r.point_diff > 0 ? "text-[#34C759]" : r.point_diff < 0 ? "text-[#FF3B30]" : "text-white"}`}>{r.point_diff > 0 ? "+" : ""}{r.point_diff}</td>
                <td className="p-2 md:p-3 text-center hidden sm:table-cell">
                  <span className={`font-mono text-xs px-1.5 py-0.5 border ${r.streak.startsWith("G") ? "border-[#34C759] text-[#34C759]" : r.streak.startsWith("P") ? "border-[#FF3B30] text-[#FF3B30]" : r.streak.startsWith("E") ? "border-[#FF9F0A] text-[#FF9F0A]" : "border-[#27272A] text-[#71717A]"}`}>{r.streak}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[#71717A] mt-2 px-1">Orden: PG → % → DIF → PF</div>
    </div>
  );
}

function CompetitionConfig({ comp, canAdmin, onDeleted }) {
  const [d, setD] = useState({
    name: comp.name, season: comp.season || "", start_date: comp.start_date || "",
    end_date: comp.end_date || "", status: comp.status, is_archived: !!comp.is_archived,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/fixture/competitions/${comp.id}`, d); toast.success("Guardado"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Eliminar DEFINITIVAMENTE la competencia "${comp.name}"? Borra en cascada TODAS sus jornadas, partidos y eventos del calendario.`)) return;
    try { await api.delete(`/fixture/competitions/${comp.id}`); toast.success("Eliminada"); onDeleted?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };
  return (
    <div className="tactical-card p-4 md:p-6 space-y-3 max-w-2xl" data-testid="comp-config">
      {!canAdmin && <div className="text-xs text-[#FF9F0A]">Solo lectura. Necesitas rol admin para editar esta competencia.</div>}
      <Field label="Nombre"><input className="input-tactical" value={d.name} onChange={e => setD({...d, name: e.target.value})} disabled={!canAdmin}/></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Temporada"><input className="input-tactical" value={d.season} onChange={e => setD({...d, season: e.target.value})} disabled={!canAdmin}/></Field>
        <Field label="Estado">
          <select className="input-tactical" value={d.status} onChange={e => setD({...d, status: e.target.value})} disabled={!canAdmin}>
            <option value="draft">Borrador</option><option value="active">Activa</option><option value="finished">Finalizada</option><option value="archived">Archivada</option>
          </select>
        </Field>
        <Field label="Fecha inicio"><input type="date" className="input-tactical" value={d.start_date} onChange={e => setD({...d, start_date: e.target.value})} disabled={!canAdmin}/></Field>
        <Field label="Fecha fin"><input type="date" className="input-tactical" value={d.end_date} onChange={e => setD({...d, end_date: e.target.value})} disabled={!canAdmin}/></Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
        <input type="checkbox" checked={d.is_archived} onChange={e => setD({...d, is_archived: e.target.checked})} disabled={!canAdmin} data-testid="cfg-archived"/>
        <span>Archivar competencia (queda oculta del selector por defecto)</span>
      </label>
      {canAdmin && (
        <div className="flex flex-col sm:flex-row justify-between gap-2 pt-3 border-t border-[#27272A]">
          <button onClick={del} className="text-xs uppercase tracking-widest px-3 py-2 border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 flex items-center justify-center gap-1" data-testid="cfg-delete"><Trash2 size={12}/> Eliminar competencia</button>
          <button onClick={save} disabled={saving} className="btn-primary text-xs" data-testid="cfg-save">{saving ? "Guardando…" : "Guardar cambios"}</button>
        </div>
      )}
    </div>
  );
}

